import { resolveSection } from "./resolveField";
import { addDropdownList } from "./addDropdownList";

// =============================================================================
// COLUMN INDEX TO LETTER
// Converts a zero-based column index to an Excel column letter.
// Handles columns beyond Z (AA, AB, AC... ZZ etc.)
// 0 = A, 1 = B, 25 = Z, 26 = AA, 27 = AB ...
// =============================================================================
const columnIndexToLetter = (index) => {
  let letter = "";
  let n = index + 1; // convert to 1-based
  while (n > 0) {
    const remainder = (n - 1) % 26;
    letter = String.fromCharCode(65 + remainder) + letter;
    n = Math.floor((n - 1) / 26);
  }
  return letter;
};

// =============================================================================
// STYLE HEADER ROW
// Applies the standard green header style used across all employee Excel sheets.
// =============================================================================
const styleHeaderRow = (row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: "center" };
    cell.border = {
      top:    { style: "thin" },
      left:   { style: "thin" },
      bottom: { style: "thin" },
      right:  { style: "thin" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "92d050" },
    };
  });
};

// =============================================================================
// BUILD WORKSHEET — TEMPLATE MODE
// Builds a worksheet with headers and one sample data row.
// Used by generateDownloadTemplate() and generateDownloadForBulkUpdateTemplate().
//
// @param workbook      - ExcelJS workbook instance
// @param worksheetName - name of the worksheet tab
// @param section       - raw section from EMPLOYEE_CONFIG (not yet resolved)
// @param refs          - reference data from fetchAllReferenceData()
// @param options       - optional config
//   @param options.prependFields  - extra fields to add BEFORE the section fields
//                                   e.g. [{ key: "employeeId", label: "EmployeeId", ... }]
//                                   useful for sheets that need EmployeeId as first column
//
// Returns the worksheet instance in case the caller needs it for further customization.
// =============================================================================
export const buildTemplateWorksheet = async (
  workbook,
  worksheetName,
  section,
  refs,
  options = {}
) => {
  const { prependFields = [] } = options;

  // Resolve the section — evaluates all functions, applies defaults,
  // filters out feature-flagged fields that are off for this client
  const resolved = resolveSection(section);

  // If the entire section is hidden for this client, skip the worksheet
  if (!resolved.visible) return null;

  const ws = workbook.addWorksheet(worksheetName);

  // Combine prepended fields with the section's visible fields
  // prependFields are already plain objects (no functions), no need to resolve
  const allFields = [...prependFields, ...resolved.visibleFields];

  // --- Step 1: Set column widths ---
  ws.columns = allFields.map((f) => ({
    key: f.key,
    width: f.width,
  }));

  // --- Step 2: Add header row ---
  const headerRow = ws.addRow(allFields.map((f) => f.label));
  styleHeaderRow(headerRow);

  // --- Step 3: Add sample data row ---
  const sampleRow = {};
  allFields.forEach((f) => {
    sampleRow[f.key] = f.sampleValue ?? "";
  });
  ws.addRow(sampleRow);

  // --- Step 4: Add dropdowns ---
  // Column letters are calculated automatically from field position
  // columnIndexToLetter handles beyond Z (AA, AB, etc.)
  for (let i = 0; i < allFields.length; i++) {
    const field = allFields[i];

    if (!field.dropdownList) continue;

    const colLetter = columnIndexToLetter(i); // 0=A, 25=Z, 26=AA, 27=AB ...

    await addDropdownList(workbook, ws, {
      column:   colLetter,
      startRow: 2,
      endRow:   1000,
      listName: field.dropdownList.listName,
      data:     field.dropdownList.data(refs),
    });
  }

  return ws;
};

// =============================================================================
// BUILD WORKSHEET — MASTERLIST MODE
// Builds a worksheet with headers and actual employee data rows.
// Used by generateDownloadMasterlist() and generateDownloadEmployeeInformation().
//
// @param workbook      - ExcelJS workbook instance
// @param worksheetName - name of the worksheet tab
// @param section       - raw section from EMPLOYEE_CONFIG (not yet resolved)
// @param employees     - array of employee objects from the API
// @param options       - optional config
//   @param options.prependFields  - extra fields to add BEFORE the section fields
//   @param options.getRowValues   - custom function to extract row values per employee
//                                   (employee, visibleFields) => array of values
//                                   only needed when data mapping is non-standard
//
// Returns the worksheet instance.
// =============================================================================
export const buildMasterlistWorksheet = (
  workbook,
  worksheetName,
  section,
  employees,
  options = {}
) => {
  const { prependFields = [], getRowValues = null } = options;

  const resolved = resolveSection(section);

  if (!resolved.visible) return null;

  const ws = workbook.addWorksheet(worksheetName);

  const allFields = [...prependFields, ...resolved.visibleFields];

  // --- Step 1: Set column widths ---
  ws.columns = allFields.map((f) => ({
    key: f.key,
    width: f.width,
  }));

  // --- Step 2: Add header row ---
  const headerRow = ws.addRow(allFields.map((f) => f.label));
  styleHeaderRow(headerRow);

  // --- Step 3: Add data rows ---
  employees.forEach((employee) => {
    if (getRowValues) {
      // Custom mapping provided by caller — used for non-standard sections
      // e.g. array sections like employeeBankAccounts that have multiple rows per employee
      const rows = getRowValues(employee, allFields);
      // getRowValues can return multiple rows for array sections
      if (Array.isArray(rows[0])) {
        rows.forEach((row) => ws.addRow(row));
      } else {
        ws.addRow(rows);
      }
    } else {
      // Standard mapping — reads from the section's apiKey on the employee object
      // e.g. section.apiKey = "employeeInformation" reads employee.employeeInformation[field.key]
      const apiKey = resolved.apiKey;
      const source = apiKey ? employee[apiKey] : employee;

      if (!source) {
        // Section data missing for this employee — add empty row with just employeeId
        ws.addRow([employee.employeeId, employee.fullName]);
        return;
      }

      const row = allFields.map((f) => source[f.key] ?? "");
      ws.addRow(row);
    }
  });

  return ws;
};